import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButton,
  ToggleButtonGroup,
  Slider,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Speed as SpeedIcon,
  Repeat as RevisitIcon,
  List as ListIcon,
  Info as InfoIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';

interface DwellAnalysisProps {
  selectedStudents: string[];
  dwellLocations: any[]; // Pre-loaded dwell data from parent
  sites?: any[]; // DwellSite[] from dwellDetection
  onReanalyze?: (params: { distanceThreshold: number, timeThreshold: number, siteRadius: number }) => void;
  onZoomToLocation?: (lat: number, lng: number, zoom?: number) => void;
  onResetView?: () => void; // Called when deselecting a location to return to original view
  // Geocoding options
  enablePlaceNames?: boolean;
  setEnablePlaceNames?: (enabled: boolean) => void;
  isEnrichingSites?: boolean;
}

interface DwellData {
  id: number | string;
  device_reference_id?: number;
  entity_id: string;
  device_id?: string;
  device_type: string;
  course_id?: string;
  latitude: number;
  longitude: number;
  duration?: string;
  duration_seconds: number;
  start_time: string;
  end_time: string;
}

const DwellAnalysis: React.FC<DwellAnalysisProps> = ({
  selectedStudents,
  dwellLocations,
  sites = [],
  onReanalyze,
  onZoomToLocation,
  onResetView,
  enablePlaceNames = false,
  setEnablePlaceNames,
  isEnrichingSites = false,
}) => {
  const [viewMode, setViewMode] = useState<'dwells' | 'sites' | 'colocation'>('dwells');
  const [filteredDwells, setFilteredDwells] = useState<DwellData[]>([]);
  const [isRunningDetection, setIsRunningDetection] = useState(false);
  
  // Track selected location for toggle behavior (click to zoom, click again to reset)
  const [selectedLocationId, setSelectedLocationId] = useState<string | number | null>(null);
  
  // Detection Parameters State
  const [detectDist, setDetectDist] = useState(100);
  const [detectTime, setDetectTime] = useState(300); // seconds
  const [siteRadius, setSiteRadius] = useState(150);
  const [deviceTypeFilter] = useState<string>('all');
  const [minDuration] = useState<number>(0);
  
  // Sorting state for Dwells table
  const [dwellSortBy, setDwellSortBy] = useState<'entity_id' | 'device_id' | 'location' | 'start_time' | 'duration_seconds'>('start_time');
  const [dwellSortOrder, setDwellSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Sorting state for Sites table
  const [siteSortBy, setSiteSortBy] = useState<'name' | 'device_count' | 'visit_count' | 'total_duration' | 'unique_days'>('visit_count');
  const [siteSortOrder, setSiteSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Sorting state for Co-Location table
  const [colocSortBy, setColocSortBy] = useState<'name' | 'device_count' | 'visit_count' | 'total_duration' | 'unique_days'>('device_count');
  const [colocSortOrder, setColocSortOrder] = useState<'asc' | 'desc'>('desc');

  // Handle run detection with loading state
  const handleRunDetection = async () => {
    if (!onReanalyze) return;
    
    setIsRunningDetection(true);
    try {
      await onReanalyze({
        distanceThreshold: detectDist,
        timeThreshold: detectTime,
        siteRadius: siteRadius
      });
    } finally {
      // Small delay to ensure UI updates are visible
      setTimeout(() => {
        setIsRunningDetection(false);
      }, 300);
    }
  };

  // Apply filters and sorting whenever dwells or filter criteria change
  useEffect(() => {
    
    let filtered: DwellData[] = [...dwellLocations];

    // Filter by selected students
    // Handle GN- prefix and case-insensitive matching
    if (selectedStudents.length > 0) {
      filtered = filtered.filter((dwell) => {
        const dwellId = (dwell.entity_id || '').toLowerCase().replace(/^gn-/, '');
        return selectedStudents.some(s => {
          const studentId = s.toLowerCase().replace(/^gn-/, '');
          return studentId === dwellId;
        });
      });
    }

    // Filter by device type
    if (deviceTypeFilter !== 'all') {
      filtered = filtered.filter(
        (dwell) => dwell.device_type.toLowerCase() === deviceTypeFilter.toLowerCase()
      );
    }

    // Filter by minimum duration
    if (minDuration > 0) {
      filtered = filtered.filter((dwell) => dwell.duration_seconds >= minDuration * 60);
    }

    // Sort based on selected column
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (dwellSortBy) {
        case 'entity_id':
          comparison = (a.entity_id || '').localeCompare(b.entity_id || '');
          break;
        case 'device_id':
          comparison = (a.device_id || '').localeCompare(b.device_id || '');
          break;
        case 'location':
          comparison = a.latitude - b.latitude || a.longitude - b.longitude;
          break;
        case 'duration_seconds':
          comparison = a.duration_seconds - b.duration_seconds;
          break;
        case 'start_time':
        default:
          comparison = new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
          break;
      }
      return dwellSortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredDwells(filtered);
  }, [dwellLocations, selectedStudents, deviceTypeFilter, minDuration, dwellSortBy, dwellSortOrder]);
  
  // Handle sort click for Dwells table
  const handleDwellSort = (column: typeof dwellSortBy) => {
    if (dwellSortBy === column) {
      setDwellSortOrder(dwellSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setDwellSortBy(column);
      setDwellSortOrder('desc');
    }
  };
  
  // Handle sort click for Sites table
  const handleSiteSort = (column: typeof siteSortBy) => {
    if (siteSortBy === column) {
      setSiteSortOrder(siteSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSiteSortBy(column);
      setSiteSortOrder('desc');
    }
  };
  
  // Handle sort click for Co-Location table
  const handleColocSort = (column: typeof colocSortBy) => {
    if (colocSortBy === column) {
      setColocSortOrder(colocSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setColocSortBy(column);
      setColocSortOrder('desc');
    }
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format date/time
  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get device type color
  const getDeviceTypeColor = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'phone':
        return 'success';
      case 'watch':
        return 'info';
      case 'vehicle':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Calculate statistics
  const stats = {
    totalDwells: filteredDwells.length,
    totalDuration: filteredDwells.reduce((sum, d) => sum + d.duration_seconds, 0),
    avgDuration:
      filteredDwells.length > 0
        ? filteredDwells.reduce((sum, d) => sum + d.duration_seconds, 0) / filteredDwells.length
        : 0,
    longestDwell: filteredDwells.length > 0
      ? Math.max(...filteredDwells.map((d) => d.duration_seconds))
      : 0,
  };

  // Site stats
  const siteStats = {
    totalSites: sites.length,
    totalRevisits: sites.filter(s => s.is_revisit).length,
    avgVisits: sites.length > 0 
      ? sites.reduce((sum, s) => sum + s.visit_count, 0) / sites.length 
      : 0
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <LocationIcon sx={{ fontSize: 18 }} />
        PoL Analysis
        <Tooltip 
          title={
            <Box sx={{ p: 1 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                Pattern of Life Analysis
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Analyzes device movement patterns to identify significant locations and routines.
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
                View Modes:
              </Typography>
              <Typography variant="body2" component="div">
                <b>• Dwells:</b> Individual stop events per device<br/>
                <b>• Repeated Visits:</b> Locations visited on multiple days<br/>
                <b>• Co-Location:</b> Sites where 2+ unique devices were present
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
                Column Definitions:
              </Typography>
              <Typography variant="body2" component="div">
                <b>• Devices:</b> Number of unique devices at this location<br/>
                <b>• Visits:</b> Total visits by all devices<br/>
                <b>• Total Time:</b> Cumulative time spent across all visits<br/>
                <b>• Unique Days:</b> Different days the site was visited<br/>
                <b>• Revisit:</b> "Yes" if visited on 2+ different days
              </Typography>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
                Pattern Insights:
              </Typography>
              <Typography variant="body2" component="div">
                <b>• High visits + High unique days:</b> Home/Work (routine location)<br/>
                <b>• Low visits + High unique days:</b> Transit point (passes through)<br/>
                <b>• Multiple devices + Same location:</b> Potential meeting or shared interest
              </Typography>
            </Box>
          }
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'rgba(30, 30, 30, 0.95)',
                maxWidth: 400,
                '& .MuiTooltip-arrow': {
                  color: 'rgba(30, 30, 30, 0.95)',
                },
              },
            },
          }}
        >
          <InfoIcon sx={{ fontSize: 16, ml: 0.5, color: 'primary.main', cursor: 'help' }} />
        </Tooltip>
      </Typography>

      {/* View Toggle */}
      <Box sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => newMode && setViewMode(newMode)}
          size="small"
          fullWidth
        >
          <ToggleButton value="dwells">
            <ListIcon sx={{ mr: 0.5, fontSize: 16 }} /> Dwells
          </ToggleButton>
          <ToggleButton value="sites">
            <RevisitIcon sx={{ mr: 0.5, fontSize: 16 }} /> Repeated Visits
          </ToggleButton>
          <ToggleButton value="colocation">
            <GroupsIcon sx={{ mr: 0.5, fontSize: 16 }} /> Co-Location
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Detection Parameters */}
      <Accordion defaultExpanded={false} sx={{ mb: 1.5 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '&.Mui-expanded': { minHeight: 40 } }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Detection Parameters
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1, pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" gutterBottom sx={{ mb: 0 }}>
                  Dwell Distance Threshold: {detectDist}m
                </Typography>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                        Dwell Distance Threshold
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                        Maximum distance (in meters) between consecutive GPS points to consider them part of the same dwell location. 
                        Smaller values detect more precise locations but may miss slow movement. 
                        Larger values group nearby points together but may merge distinct locations.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Slider
                value={detectDist}
                onChange={(_, val) => setDetectDist(val as number)}
                min={10}
                max={500}
                step={10}
                size="small"
                valueLabelDisplay="auto"
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" gutterBottom sx={{ mb: 0 }}>
                  Min Dwell Time: {Math.round(detectTime / 60)} min
                </Typography>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                        Minimum Dwell Time
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                        Minimum duration (in minutes) a device must remain within the distance threshold to be considered a "dwell" (stop). 
                        Shorter times detect brief stops but may include slow movement. 
                        Longer times only detect significant stops but may miss quick visits.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Slider
                value={detectTime}
                onChange={(_, val) => setDetectTime(val as number)}
                min={60}
                max={3600}
                step={60}
                size="small"
                valueLabelDisplay="auto"
                valueLabelFormat={(val) => `${val/60}m`}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" gutterBottom sx={{ mb: 0 }}>
                  Site Clustering Radius: {siteRadius}m
                </Typography>
                <Tooltip 
                  title={
                    <Box>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                        Site Clustering Radius
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontSize: '0.75rem' }}>
                        Maximum distance (in meters) between dwell locations to group them into the same "site" for repeated visit analysis. 
                        Smaller values create more precise sites but may split nearby visits into separate sites. 
                        Larger values group more visits together but may merge distinct locations. 
                        Default: 150m balances precision with grouping.
                      </Typography>
                    </Box>
                  }
                  arrow
                  placement="right"
                >
                  <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                </Tooltip>
              </Box>
              <Slider
                value={siteRadius}
                onChange={(_, val) => setSiteRadius(val as number)}
                min={50}
                max={1000}
                step={50}
                size="small"
                valueLabelDisplay="auto"
              />
            </Box>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={handleRunDetection}
              disabled={!onReanalyze || isRunningDetection}
              startIcon={isRunningDetection ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {isRunningDetection ? 'Running Detection...' : 'Run Detection'}
            </Button>
            
            {/* Place Names Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" fontWeight="medium">
                  🗺️ Enrich with Place Names
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
                  Uses Mapbox API to identify locations (slower initial load)
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isEnrichingSites && <CircularProgress size={14} />}
                <ToggleButtonGroup
                  value={enablePlaceNames ? 'on' : 'off'}
                  exclusive
                  onChange={(_, val) => setEnablePlaceNames?.(val === 'on')}
                  size="small"
                  sx={{ height: 24 }}
                >
                  <ToggleButton value="off" sx={{ px: 1, py: 0, fontSize: '0.65rem' }}>
                    Off
                  </ToggleButton>
                  <ToggleButton value="on" sx={{ px: 1, py: 0, fontSize: '0.65rem' }}>
                    On
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>

      {viewMode === 'dwells' && (
        <>
          {/* Show message if no data loaded */}
          {dwellLocations.length === 0 && (
            <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
              <Typography variant="caption">No dwell data available. Click "Load" in the main panel to fetch data.</Typography>
            </Alert>
          )}

          {/* Only show filters and data if we have dwells */}
          {dwellLocations.length > 0 && (
            <>
              {/* Compact Statistics Bar */}
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1.5, py: 0.75, bgcolor: 'background.default', borderRadius: 1 }}>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Typography sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                    {stats.totalDwells}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Dwells</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Typography sx={{ color: 'secondary.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                    {formatDuration(stats.totalDuration)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Total</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                    {formatDuration(stats.avgDuration)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Average</Typography>
                </Box>
                <Box sx={{ textAlign: 'center', px: 1 }}>
                  <Typography sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                    {formatDuration(stats.longestDwell)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Longest</Typography>
                </Box>
              </Box>

              {/* Dwells Table */}
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Dwell Locations ({filteredDwells.length})
                </Typography>
                
                <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <TableSortLabel
                            active={dwellSortBy === 'entity_id'}
                            direction={dwellSortBy === 'entity_id' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('entity_id')}
                          >
                            Entity
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <TableSortLabel
                            active={dwellSortBy === 'device_id'}
                            direction={dwellSortBy === 'device_id' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('device_id')}
                          >
                            Device
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <TableSortLabel
                            active={dwellSortBy === 'location'}
                            direction={dwellSortBy === 'location' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('location')}
                          >
                            Location
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <TableSortLabel
                            active={dwellSortBy === 'start_time'}
                            direction={dwellSortBy === 'start_time' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('start_time')}
                          >
                            Start Time
                          </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.75rem' }}>
                          <TableSortLabel
                            active={dwellSortBy === 'duration_seconds'}
                            direction={dwellSortBy === 'duration_seconds' ? dwellSortOrder : 'asc'}
                            onClick={() => handleDwellSort('duration_seconds')}
                          >
                            Duration
                          </TableSortLabel>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDwells.map((dwell) => (
                        <TableRow 
                          key={dwell.id} 
                          hover 
                          selected={selectedLocationId === dwell.id}
                          onClick={() => {
                            if (selectedLocationId === dwell.id) {
                              // Clicking same row - deselect and reset view
                              setSelectedLocationId(null);
                              onResetView?.();
                            } else {
                              // Clicking new row - select and zoom
                              setSelectedLocationId(dwell.id);
                              onZoomToLocation?.(dwell.latitude, dwell.longitude, 16);
                            }
                          }}
                          sx={{ 
                            cursor: onZoomToLocation ? 'pointer' : 'default',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(25, 118, 210, 0.15)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.25)',
                              }
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                              {dwell.entity_id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={dwell.device_type}
                              size="small"
                              color={getDeviceTypeColor(dwell.device_type) as any}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {dwell.latitude.toFixed(5)}, {dwell.longitude.toFixed(5)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <TimeIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                {formatDateTime(dwell.start_time)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SpeedIcon fontSize="small" sx={{ fontSize: '0.875rem' }} />
                              <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                {formatDuration(dwell.duration_seconds)}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredDwells.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              No dwells match the current filters
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
          )}
        </>
      )}

      {viewMode === 'sites' && (
        <>
          {/* Sites View - Only show sites with repeated visits */}
          {(() => {
            // Filter to only show sites that are revisits (visit_count > 1 or is_revisit === true)
            const revisitedSites = sites.filter(site => site.is_revisit || site.visit_count > 1);
            
            return revisitedSites.length === 0 ? (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="caption">No repeated visit sites detected. Sites with only 1 visit are shown in the "Individual Dwells" tab.</Typography>
              </Alert>
            ) : (
              <>
                {/* Compact Site Statistics Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1.5, py: 0.75, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {siteStats.totalSites}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Total Sites</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {siteStats.totalRevisits}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Revisited</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {siteStats.avgVisits.toFixed(1)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Avg Visits</Typography>
                  </Box>
                </Box>

                {/* Sites Table - Only show revisited sites */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Repeated Visit Sites ({revisitedSites.length})
                  </Typography>
                  
                  <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={siteSortBy === 'name'}
                              direction={siteSortBy === 'name' ? siteSortOrder : 'asc'}
                              onClick={() => handleSiteSort('name')}
                            >
                              Site Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={siteSortBy === 'device_count'}
                              direction={siteSortBy === 'device_count' ? siteSortOrder : 'asc'}
                              onClick={() => handleSiteSort('device_count')}
                            >
                              Devices
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={siteSortBy === 'visit_count'}
                              direction={siteSortBy === 'visit_count' ? siteSortOrder : 'asc'}
                              onClick={() => handleSiteSort('visit_count')}
                            >
                              Visits
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={siteSortBy === 'total_duration'}
                              direction={siteSortBy === 'total_duration' ? siteSortOrder : 'asc'}
                              onClick={() => handleSiteSort('total_duration')}
                            >
                              Total Time
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>Type</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={siteSortBy === 'unique_days'}
                              direction={siteSortBy === 'unique_days' ? siteSortOrder : 'asc'}
                              onClick={() => handleSiteSort('unique_days')}
                            >
                              Unique Days
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>Revisit?</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...revisitedSites].sort((a, b) => {
                          let comparison = 0;
                          switch (siteSortBy) {
                            case 'name':
                              comparison = (a.name || '').localeCompare(b.name || '');
                              break;
                            case 'device_count':
                              comparison = (a.unique_device_count || a.device_ids?.length || 0) - (b.unique_device_count || b.device_ids?.length || 0);
                              break;
                            case 'visit_count':
                              comparison = (a.visit_count || 0) - (b.visit_count || 0);
                              break;
                            case 'total_duration':
                              comparison = (a.total_duration || 0) - (b.total_duration || 0);
                              break;
                            case 'unique_days':
                              comparison = (a.unique_days || 0) - (b.unique_days || 0);
                              break;
                          }
                          return siteSortOrder === 'asc' ? comparison : -comparison;
                        }).map((site) => (
                        <TableRow 
                          key={site.id} 
                          hover
                          selected={selectedLocationId === `site-${site.id}`}
                          onClick={() => {
                            const siteKey = `site-${site.id}`;
                            if (selectedLocationId === siteKey) {
                              // Clicking same row - deselect and reset view
                              setSelectedLocationId(null);
                              onResetView?.();
                            } else {
                              // Clicking new row - select and zoom
                              setSelectedLocationId(siteKey);
                              onZoomToLocation?.(site.latitude, site.longitude, 16);
                            }
                          }}
                          sx={{ 
                            cursor: onZoomToLocation ? 'pointer' : 'default',
                            '&.Mui-selected': {
                              backgroundColor: 'rgba(25, 118, 210, 0.15)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.25)',
                              }
                            }
                          }}
                        >
                          <TableCell>
                            <Typography variant="caption" fontWeight="bold" display="block">
                              {site.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                              {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip 
                              title={
                                site.device_ids && site.device_ids.length > 0 
                                  ? site.device_ids.map((id: string) => id.substring(0, 12) + (id.length > 12 ? '...' : '')).join(', ')
                                  : 'No device info'
                              }
                              arrow
                            >
                              <Chip 
                                label={site.unique_device_count || (site.device_ids?.length || 1)} 
                                size="small" 
                                color={site.unique_device_count > 1 ? 'warning' : 'default'} 
                                sx={{ height: 20, fontSize: '0.7rem' }} 
                              />
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <Chip label={site.visit_count} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {formatDuration(site.total_duration)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={site.type === 'dwell' ? 'Dwell' : 'Pass-Through'} 
                              size="small" 
                              color={site.type === 'dwell' ? 'primary' : 'default'} 
                              variant={site.type === 'dwell' ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: '0.7rem' }} 
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption">
                              {site.unique_days}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {site.is_revisit ? (
                              <Chip label="Yes" size="small" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                            ) : (
                              <Typography variant="caption" color="text.secondary">No</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </>
            );
          })()}
        </>
      )}

      {/* Co-Location View - Sites with 2+ unique devices */}
      {viewMode === 'colocation' && (
        <>
          {(() => {
            // Filter to only show sites where 2+ unique devices were present
            const coLocationSites = sites.filter(site => 
              (site.unique_device_count && site.unique_device_count >= 2) || 
              (site.device_ids && site.device_ids.length >= 2)
            );
            
            // Sort based on selected column
            const sortedCoLocationSites = [...coLocationSites].sort((a, b) => {
              let comparison = 0;
              switch (colocSortBy) {
                case 'name':
                  comparison = (a.name || '').localeCompare(b.name || '');
                  break;
                case 'device_count':
                  comparison = (a.unique_device_count || a.device_ids?.length || 0) - (b.unique_device_count || b.device_ids?.length || 0);
                  break;
                case 'visit_count':
                  comparison = (a.visit_count || 0) - (b.visit_count || 0);
                  break;
                case 'total_duration':
                  comparison = (a.total_duration || 0) - (b.total_duration || 0);
                  break;
                case 'unique_days':
                  comparison = (a.unique_days || 0) - (b.unique_days || 0);
                  break;
              }
              return colocSortOrder === 'asc' ? comparison : -comparison;
            });
            
            return sortedCoLocationSites.length === 0 ? (
              <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                <Typography variant="caption">
                  No co-location sites detected. Co-location sites are areas where 2 or more unique devices were present.
                </Typography>
              </Alert>
            ) : (
              <>
                {/* Compact Co-Location Statistics Bar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-around', mb: 1.5, py: 0.75, bgcolor: 'background.default', borderRadius: 1 }}>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'secondary.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {sortedCoLocationSites.length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Sites</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'warning.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {sortedCoLocationSites.length > 0 
                        ? Math.max(...sortedCoLocationSites.map(s => s.unique_device_count || s.device_ids?.length || 0))
                        : 0}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Max Devices</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'info.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {Array.from(new Set(sortedCoLocationSites.flatMap(s => s.device_ids || []))).length}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Unique Devices</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center', px: 1 }}>
                    <Typography sx={{ color: 'success.main', fontWeight: 'bold', fontSize: '1rem', lineHeight: 1.2 }}>
                      {sortedCoLocationSites.length > 0 
                        ? (sortedCoLocationSites.reduce((sum, s) => sum + (s.unique_device_count || s.device_ids?.length || 0), 0) / sortedCoLocationSites.length).toFixed(1)
                        : 0}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>Avg/Site</Typography>
                  </Box>
                </Box>

                {/* Co-Location Sites Table */}
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <GroupsIcon sx={{ fontSize: 16, color: 'secondary.main' }} />
                    Co-Location Sites ({sortedCoLocationSites.length})
                    <Tooltip 
                      title="Locations where 2 or more unique devices were detected. This may indicate meetings, shared locations, or coordinated activity."
                      arrow
                    >
                      <InfoIcon sx={{ fontSize: 14, ml: 0.5, color: 'primary.main', cursor: 'help' }} />
                    </Tooltip>
                  </Typography>
                  
                  <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)' }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={colocSortBy === 'name'}
                              direction={colocSortBy === 'name' ? colocSortOrder : 'asc'}
                              onClick={() => handleColocSort('name')}
                            >
                              Site Name
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={colocSortBy === 'device_count'}
                              direction={colocSortBy === 'device_count' ? colocSortOrder : 'asc'}
                              onClick={() => handleColocSort('device_count')}
                            >
                              Devices
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>Device IDs</TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={colocSortBy === 'visit_count'}
                              direction={colocSortBy === 'visit_count' ? colocSortOrder : 'asc'}
                              onClick={() => handleColocSort('visit_count')}
                            >
                              Total Visits
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={colocSortBy === 'total_duration'}
                              direction={colocSortBy === 'total_duration' ? colocSortOrder : 'asc'}
                              onClick={() => handleColocSort('total_duration')}
                            >
                              Total Time
                            </TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.75rem' }}>
                            <TableSortLabel
                              active={colocSortBy === 'unique_days'}
                              direction={colocSortBy === 'unique_days' ? colocSortOrder : 'asc'}
                              onClick={() => handleColocSort('unique_days')}
                            >
                              Unique Days
                            </TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedCoLocationSites.map((site) => (
                          <TableRow 
                            key={site.id} 
                            hover 
                            selected={selectedLocationId === `coloc-${site.id}`}
                            onClick={() => {
                              const siteKey = `coloc-${site.id}`;
                              if (selectedLocationId === siteKey) {
                                // Clicking same row - deselect and reset view
                                setSelectedLocationId(null);
                                onResetView?.();
                              } else {
                                // Clicking new row - select and zoom
                                setSelectedLocationId(siteKey);
                                onZoomToLocation?.(site.latitude, site.longitude, 16);
                              }
                            }}
                            sx={{ 
                              backgroundColor: 'rgba(255, 152, 0, 0.05)',
                              cursor: onZoomToLocation ? 'pointer' : 'default',
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(255, 152, 0, 0.2)',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 152, 0, 0.3)',
                                }
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="caption" fontWeight="bold" display="block">
                                {site.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                                {site.latitude.toFixed(5)}, {site.longitude.toFixed(5)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={site.unique_device_count || site.device_ids?.length || 0} 
                                size="small" 
                                color="warning" 
                                sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} 
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(site.device_ids || []).slice(0, 3).map((deviceId: string, idx: number) => (
                                  <Chip 
                                    key={idx}
                                    label={deviceId.substring(0, 8) + '...'}
                                    size="small" 
                                    variant="outlined"
                                    sx={{ height: 18, fontSize: '0.6rem' }} 
                                  />
                                ))}
                                {(site.device_ids?.length || 0) > 3 && (
                                  <Tooltip title={site.device_ids?.slice(3).join(', ')}>
                                    <Chip 
                                      label={`+${site.device_ids!.length - 3}`}
                                      size="small" 
                                      sx={{ height: 18, fontSize: '0.6rem' }} 
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip label={site.visit_count} size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDuration(site.total_duration)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {site.unique_days}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </>
            );
          })()}
        </>
      )}
    </Box>
  );
};

export default DwellAnalysis;
